import CloseButton from '@orn/shared-web/components/Button/CloseButton';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import BenchmarkTranscriptPane from './BenchmarkTranscriptPane';
import { buildRunList, type IArmRuns, type TranscriptArm } from './transcriptModalView';

import type { IMopsAssistantBenchmarkScenarioTranscriptsV1 } from '@orn/global/assistant/MopsAssistantBenchmarkArtifact.js';

/** Which run the right pane is showing — an arm + iteration coordinate. */
interface IRunSelection {
  arm: TranscriptArm;
  iterationIndex: number;
}

/** The run to select on open: head's first (the candidate), else base's first, else none. */
const firstSelectableRun = (groups: IArmRuns[]): IRunSelection | null => {
  const head = groups.find((g) => g.arm === 'head');
  const base = groups.find((g) => g.arm === 'base');
  const pick = head?.runs[0] ?? base?.runs[0] ?? null;
  return pick === null ? null : { arm: pick.arm, iterationIndex: pick.iterationIndex };
};

interface IPassBadgeProps {
  passed: boolean;
}

const PassBadge = ({ passed }: IPassBadgeProps) => (
  <span
    className={cx(
      'rounded-sm border px-1 text-[9px] font-semibold uppercase',
      passed
        ? 'border-fg-success-primary/50 text-fg-success-primary'
        : 'border-fg-error-primary/50 text-fg-error-primary'
    )}
  >
    {passed ? 'pass' : 'fail'}
  </span>
);

interface IRunListProps {
  groups: IArmRuns[];
  selected: IRunSelection | null;
  onSelect: (selection: IRunSelection) => void;
}

/** The left rail: a group per arm, each captured run tagged pass/fail with its roll-up. */
const RunList = ({ groups, selected, onSelect }: IRunListProps) => (
  <div className="border-border-primary overflow-y-auto border-r">
    {groups.map((group) => (
      <div key={group.arm}>
        <div className="bg-bg-secondary text-fg-tertiary sticky top-0 z-10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider">
          {group.arm}
        </div>
        {group.runs.length === 0 ? (
          <div className="text-fg-quaternary px-3 py-2 text-[11px] italic">No capture</div>
        ) : (
          group.runs.map((run) => {
            const isSelected = selected?.arm === group.arm && selected.iterationIndex === run.iterationIndex;
            return (
              <button
                key={run.iterationIndex}
                type="button"
                onClick={() => onSelect({ arm: group.arm, iterationIndex: run.iterationIndex })}
                className={cx(
                  'focus-visible:outline-border-brand flex w-full flex-col gap-0.5 border-l-2 px-3 py-1.5 text-left focus-visible:outline focus-visible:outline-2',
                  isSelected
                    ? 'border-fg-brand-primary bg-bg-brand-secondary'
                    : 'hover:bg-bg-secondary border-transparent'
                )}
              >
                <span className="flex items-center gap-1.5 font-mono text-[11px]">
                  <PassBadge passed={run.passed} />
                  <span className="text-fg-secondary">iter {run.iterationIndex}</span>
                </span>
                <span className="text-fg-tertiary font-mono text-[10px] tabular-nums">
                  {run.rollup.display.cost} · {run.rollup.display.outputTokens} out · {run.rollup.display.latency}
                  {run.rollup.partial && (
                    <span className="text-fg-quaternary" title="Some rounds were unmeasured — totals are a floor">
                      {' '}
                      ~
                    </span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    ))}
  </div>
);

interface IBenchmarkTranscriptModalProps {
  scenarioId: string;
  transcripts: IMopsAssistantBenchmarkScenarioTranscriptsV1;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The base-vs-head transcript viewer for one scenario: a left rail listing the
 * representative captured runs per arm (each tagged pass/fail with its cost/
 * latency roll-up), and a wide scrollable pane showing the selected run's
 * transcript with per-round cost/token/latency detail. Opens defaulted to the
 * candidate (head) side.
 */
const BenchmarkTranscriptModal = ({ scenarioId, transcripts, isOpen, onClose }: IBenchmarkTranscriptModalProps) => {
  const groups = React.useMemo(() => buildRunList(transcripts), [transcripts]);
  // Default to the candidate's first run on open. The parent mounts a fresh
  // instance per scenario, so a lazy initializer (rather than a post-render
  // effect) both avoids an empty-state flash on first paint and needs no reset.
  const [selected, setSelected] = React.useState<IRunSelection | null>(() => firstSelectableRun(groups));

  const selectedIteration = React.useMemo(() => {
    if (selected === null) {
      return null;
    }
    return transcripts[selected.arm]?.find((it) => it.iterationIndex === selected.iterationIndex) ?? null;
  }, [selected, transcripts]);

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="6xl" className="overflow-hidden">
      <Modal.Header className="py-3 text-sm">
        <div className="flex w-full items-center justify-between gap-3">
          <span className="flex items-baseline gap-2">
            <span className="font-mono">{scenarioId}</span>
            <span className="text-fg-tertiary text-xs font-normal">Representative base vs head transcripts</span>
          </span>
          <CloseButton onClick={onClose} size="lg" />
        </div>
      </Modal.Header>
      <div className="border-border-primary bg-bg-primary grid h-[78vh] w-full grid-cols-[16rem_1fr] overflow-hidden border-t">
        <RunList groups={groups} selected={selected} onSelect={setSelected} />
        {/* Key on the selected run so switching runs remounts the scroll box and
            resets scrollTop — otherwise you land mid-scroll in the next run. */}
        <div key={selected ? `${selected.arm}-${selected.iterationIndex}` : 'empty'} className="overflow-y-auto">
          {selectedIteration === null ? (
            <div className="text-fg-tertiary p-6 text-center text-xs italic">
              No transcript captured for this scenario.
            </div>
          ) : (
            <BenchmarkTranscriptPane iteration={selectedIteration} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BenchmarkTranscriptModal;
